import { Component, Inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/alert.service';
import { OutputConfirmProductSearch } from 'src/app/models/products/productSearch';
import { DownloadOutputService } from 'src/app/services/download-output.service';
import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import {
  outputFormulaValueKindLabel,
  outputFormulaValueCoversStills,
  outputFormulaValueCoversVideos,
} from 'src/app/models/output-formulas/output-formula-value';
import { CreateOutputsCollectionComponent } from 'src/app/components/home/output-collections/actions/create-outputs-collection.component';
import { ProductsService } from 'src/app/services/products.service';

@Component({
    selector: 'app-generate-shopify-assets-sync',
    templateUrl: './generate-shopify-assets-sync.component.html',
    styleUrl: './generate-shopify-assets-sync.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class GenerateShopifyAssetsSyncComponent {
  formulas: OutputFormula[] = [];
  isLoading = true;
  isDownload = false;
  overwrite: FormControl = new FormControl<boolean>(false);
  outputForms: FormGroup[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public dataDialog: OutputConfirmProductSearch,
    private outputFormulaService: OutputFormulaService,
    private productsService: ProductsService,
    public dialogRef: MatDialogRef<CreateOutputsCollectionComponent>,
    private downloadOutputService: DownloadOutputService,
    public dialog: MatDialog,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.loadFormulas();
  }

  loadFormulas() {
    this.outputFormulaService.list(this.dataDialog?.organizationId).subscribe({
      next: (formulas) => {
        this.formulas = formulas;
        this.outputForms = this.formulas.map((formula) => this.buildFormulaFormGroup(formula, false));
        this.isLoading = false;
        if (isDevMode()) {
          console.log('[DEV] [GenerateShopifyAssetsSyncComponent] loaded formulas', this.formulas);
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private buildFormulaFormGroup(formula: OutputFormula, checked: boolean): FormGroup {
    return new FormGroup({
      id: new FormControl<string>(formula.id),
      name: new FormControl<string>(formula.name),
      configText: new FormControl<string>(outputFormulaValueKindLabel(formula.value.kind)),
      checked: new FormControl<boolean>(checked),
      stills: new FormControl<boolean>(outputFormulaValueCoversStills(formula.value)),
      videos: new FormControl<boolean>(outputFormulaValueCoversVideos(formula.value)),
    });
  }

  generate() {
    const outputFormatIds = this.getCheckedOutputFormatIds();
    if (!outputFormatIds) return;

    this.productsService
      .syncProductShopifyAssets({
        productIds: this.dataDialog.productIds || [],
        outputFormats: outputFormatIds,
        overwrite: this.overwrite.value,
      })
      .subscribe((res) => {
        if (isDevMode()) {
          console.log('[DEV] [GenerateShopifyAssetsSyncComponent] successfully queued');
        }
        this.alertService.successToast(
          `Success! ${res.productProcessingCount}/${res.productCount} Selected Products Queued! Output Assets will be synced shortly!`,
        );
        this.dialogRef.close();
      });
  }

  private getCheckedOutputFormatIds(): { outputFormatId: string; type: string }[] | null {
    const checkedFormats = this.outputForms.filter((format) => format.get('checked')!.value);
    const hasInvalidFormat = checkedFormats.some(
      (format) => !format.get('stills')?.value && !format.get('videos')?.value,
    );

    if (hasInvalidFormat) {
      this.alertService.errorToast('Selected Formats must have either one type or both selected!');
      return null;
    }

    return checkedFormats.map((format) => ({
      outputFormatId: format.get('id')?.value,
      type: this.resolveFormatType(format),
    }));
  }

  private resolveFormatType(format: FormGroup): string {
    const hasStills = format.get('stills')?.value;
    const hasVideos = format.get('videos')?.value;
    if (hasStills && hasVideos) return 'Combo';
    if (hasVideos) return 'Video';
    return 'Still';
  }
}
